import zod from 'zod';
import { CigamIntegrationFormSchema } from '../../../services/IntegrationService/CigamService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  useCreateCigamIntegrationAsync,
  useGetCigamIntegrationByIdAsync,
  useUpdateCigamIntegrationAsync,
} from '../../../services/IntegrationService/CigamService/hooks/useMutation';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { t } from 'i18next';

interface CigamEditionFormProps {
  cigamIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type CigamEditionFormInput = zod.infer<typeof CigamIntegrationFormSchema>;

const CigamEditionForm = ({
  cigamIdToUpdate,
  handleSaveForm,
}: CigamEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset } = useForm<CigamEditionFormInput>({
    resolver: zodResolver(CigamIntegrationFormSchema),
    defaultValues: {
      name: '',
      port: '',
      password: '',
      username: '',
      host: '',
      description: '',
      isActive: true,
    },
  });

  const { mutate: mutateCreateCigamIntegration } =
    useCreateCigamIntegrationAsync();
  const { mutate: mutateUpdateCigamIntegration } =
    useUpdateCigamIntegrationAsync();
  const { mutateAsync: mutateGetCigamIntegrationByIdAsync } =
    useGetCigamIntegrationByIdAsync();

  const handleSave = (data: CigamEditionFormInput) => {
    if (cigamIdToUpdate) {
      mutateUpdateCigamIntegration(
        { id: cigamIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração cigam atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateCigamIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração cigam criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (cigamIdToUpdate) {
      mutateGetCigamIntegrationByIdAsync(cigamIdToUpdate).then((r) =>
        reset(r, { keepDefaultValues: true }),
      );
    }
  }, [cigamIdToUpdate]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.cigamForms.name')}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.cigamForms.host')}
              />
            )}
            name={'host'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.cigamForms.door')}
              />
            )}
            name={'port'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.cigamForms.user')}
              />
            )}
            name={'username'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.cigamForms.password')}
              />
            )}
            name={'password'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.cigamForms.descriptors')}
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.cigamForms.active')}
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent fullWidth onClick={handleSubmit(handleSave)}>
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default CigamEditionForm;
